import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Loader } from 'semantic-ui-react';
import DijonCard from "../Dashboard/DijonCard";
import axiosInstance from "../../lib/auth/axiosApi";

const DAY_ABBREV = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function todayISO() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now - offset).toISOString().slice(0, 10);
}

function ExerciseRow({ exercise, loggedToday, onLog }) {
  const [open, setOpen] = useState(false);
  const [sets, setSets] = useState(exercise.sets || '');
  const [reps, setReps] = useState(exercise.reps || '');
  const [weight, setWeight] = useState(exercise.target_weight || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onLog(exercise, { sets, reps, weight });
      setOpen(false);
    } catch (e) {
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontWeight: 'bold' }}>{exercise.name}</span>
          <span style={{ opacity: 0.6, marginLeft: '8px', fontSize: '13px' }}>
            {exercise.sets}x{exercise.reps}
            {exercise.target_weight ? ` @ ${exercise.target_weight}` : ''}
          </span>
          {exercise.notes && (
            <div style={{ fontSize: '12px', opacity: 0.55 }}>{exercise.notes}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {loggedToday && (
            <span style={{ fontSize: '12px', color: '#21ba45', marginRight: '8px' }}>
              done{loggedToday.weight ? ` @ ${loggedToday.weight}` : ''}
            </span>
          )}
          <Button size="mini" basic onClick={() => setOpen(!open)}>
            {open ? 'cancel' : 'log'}
          </Button>
        </div>
      </div>

      {open && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
          <Input size="mini" value={sets} onChange={e => setSets(e.target.value)} label="sets" style={{ width: '95px' }} />
          <Input size="mini" value={reps} onChange={e => setReps(e.target.value)} label="reps" style={{ width: '110px' }} />
          <Input size="mini" value={weight} onChange={e => setWeight(e.target.value)} label="lb" style={{ width: '105px' }} />
          <Button size="mini" primary loading={saving} disabled={saving} onClick={submit}>save</Button>
          {error && <span style={{ color: '#db2828', fontSize: '12px' }}>{error}</span>}
        </div>
      )}
    </div>
  );
}

function Fitness(props) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('training-plans/active/');
      setData(res.data);
      setError(null);
    } catch (e) {
      setError('Could not load your plan.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logExercise = async (exercise, { sets, reps, weight }) => {
    await axiosInstance.post('workout-logs/', {
      exercise: exercise.id,
      exercise_name: exercise.name,
      date: todayISO(),
      sets_completed: sets || 0,
      reps_completed: reps || '',
      weight: weight === '' ? null : weight,
    });
    await load();
  };

  const plan = data && data.plan;
  const today = plan && plan.days.find(d => d.id === data.today);
  const iso = todayISO();
  const logsToday = (data && data.recent_logs ? data.recent_logs : []).filter(l => l.date === iso);
  const loggedFor = (exerciseId) => logsToday.find(l => l.exercise === exerciseId);

  return (
    <DijonCard
      width={'560px'}
      height={'560px'}
      activeCards={props.activeCards}
      setActiveCards={props.setActiveCards}
      name={'fitness'}
      content={
        <div style={{ height: '100%', overflow: 'auto', padding: '20px', boxSizing: 'border-box' }}>
          {loading && <Loader active inline='centered' />}

          {!loading && error && (
            <div style={{ color: '#db2828' }}>{error}</div>
          )}

          {!loading && !error && !plan && (
            <div style={{ opacity: 0.7, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>No active plan yet.</div>
              Build one in Django admin under <em>Training plans</em>, mark it active,
              then add days and exercises. It'll show up here.
            </div>
          )}

          {!loading && plan && (
            <>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{plan.name}</div>
              {plan.notes && (
                <div style={{ fontSize: '13px', opacity: 0.6, marginBottom: '10px' }}>{plan.notes}</div>
              )}

              <div style={{ margin: '14px 0 8px', fontSize: '13px', textTransform: 'uppercase', opacity: 0.55 }}>
                Today — {today ? today.name : 'nothing scheduled'}
              </div>

              {today && today.is_rest && (
                <div style={{ opacity: 0.7 }}>Rest day. Go play backgammon.</div>
              )}

              {today && !today.is_rest && today.exercises.length === 0 && (
                <div style={{ opacity: 0.7 }}>No exercises on this day yet.</div>
              )}

              {today && !today.is_rest && today.exercises.map(ex => (
                <ExerciseRow
                  key={ex.id}
                  exercise={ex}
                  loggedToday={loggedFor(ex.id)}
                  onLog={logExercise}
                />
              ))}

              <div style={{ margin: '20px 0 8px', fontSize: '13px', textTransform: 'uppercase', opacity: 0.55 }}>
                The week
              </div>
              {plan.days.map(day => (
                <div
                  key={day.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '5px 0',
                    fontWeight: day.id === data.today ? 'bold' : 'normal',
                    opacity: day.is_rest ? 0.5 : 1,
                  }}
                >
                  <span>{DAY_ABBREV[day.day_of_week]} — {day.name}</span>
                  <span style={{ fontSize: '12px', opacity: 0.6 }}>
                    {day.is_rest ? 'rest' : `${day.exercises.length} lifts`}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      }
    />
  );
}

export default Fitness;
